import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import {
  Box, Paper, Typography, CircularProgress,
  LinearProgress, Alert, Chip, Avatar,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { getAuthHeaders } from '../../services/authSession';

const STEP_SECONDS = 10;
const API_URL = process.env.REACT_APP_API_URL || '';

export default function MyQRScreen({ workerId, workerName }) {
  const { t } = useTranslation();
  const [qrData, setQrData]     = useState(null);
  const [timeLeft, setTimeLeft] = useState(STEP_SECONDS);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [fadeKey, setFadeKey]   = useState(0);

  const fetchQR = useCallback(async () => {
    if (!workerId) return;
    try {
      setError(null);
      const res = await fetch(`${API_URL}/api/qr/my-qr/${workerId}`, { headers: getAuthHeaders() });
      const data = await res.json();

      if (!data.success) {
        setError(data.error === 'NOT_ENROLLED'
          ? t('presence.qrNotActivated')
          : t('presence.qrLoadError'));
        return;
      }

      setQrData(data);
      const remaining = Math.max(1, Math.ceil((data.valid_until_ms - Date.now()) / 1000));
      setTimeLeft(remaining);
      setFadeKey(k => k + 1);
    } catch {
      setError(t('presence.noServerConnection'));
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  // Fetch fillestar
  useEffect(() => { fetchQR(); }, [fetchQR]);

  // Countdown + auto-refresh — ndalet kur tab-i është i fshehur
  useEffect(() => {
    if (!qrData) return;

    const tick = () => {
      if (document.hidden) return;
      setTimeLeft(prev => {
        if (prev <= 1) { fetchQR(); return STEP_SECONDS; }
        return prev - 1;
      });
    };

    const interval = setInterval(tick, 1000);

    const onVisible = () => {
      if (!document.hidden) fetchQR();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [qrData, fetchQR]);

  const urgency  = timeLeft <= 3;
  const progress = (timeLeft / STEP_SECONDS) * 100;
  const barColor = urgency ? 'error' : 'success';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{error}</Alert>
      </Box>
    );
  }

  const qrPayload = qrData
    ? JSON.stringify({ otp: qrData.current_otp, worker_id: workerId })
    : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, gap: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}>
          <QrCode2Icon />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="bold">{t('presence.myQR')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {qrData?.worker_name || workerName}
          </Typography>
        </Box>
      </Box>

      {/* QR Code */}
      <Paper
        key={fadeKey}
        elevation={4}
        sx={{
          p: 2, borderRadius: 3,
          border: `3px solid`,
          borderColor: urgency ? 'error.main' : 'success.main',
          transition: 'border-color 0.3s',
          animation: 'qrFadeIn 0.35s ease',
          '@keyframes qrFadeIn': {
            from: { opacity: 0.2, transform: 'scale(0.96)' },
            to:   { opacity: 1,   transform: 'scale(1)' },
          },
        }}
      >
        {qrPayload && (
          <QRCodeSVG
            value={qrPayload}
            size={220}
            level="M"
            includeMargin={false}
          />
        )}
      </Paper>

      {/* Countdown bar */}
      <Box sx={{ width: 240 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={barColor}
          sx={{ height: 8, borderRadius: 4 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.8 }}>
          <AccessTimeIcon fontSize="small" color={barColor} />
          <Typography
            variant="body2"
            fontWeight="bold"
            color={urgency ? 'error.main' : 'text.primary'}
          >
            {t('presence.renewsIn', { seconds: timeLeft })}
          </Typography>
        </Box>
      </Box>

      {/* Info chip */}
      <Chip
        label={t('presence.showAtKiosk')}
        variant="outlined"
        color="primary"
        size="small"
      />

      <Typography variant="caption" color="text.secondary">
        {t('presence.qrChangesEvery10s')}
      </Typography>
    </Box>
  );
}
